import { Link } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { ArrowLeft } from "lucide-react";

// v77.67 — Terms of Use rewritten for Ireland / EU.
// Tone: clear, professional, plain English. Includes explicit subscription
// and cancellation terms (€10.99/month, 3-month minimum commitment).

const Section = ({ title, id, children }) => (
  <section className="mt-10" id={id}>
    <h2 className="font-heading text-xl font-medium text-[#2E3A3F]">{title}</h2>
    <div className="mt-3 space-y-3 text-[#2E3A3F] leading-relaxed">{children}</div>
  </section>
);

export default function Terms() {
  return (
    <div className="min-h-screen bg-[#F5F7F8] text-[#2E3A3F]">
      <header className="border-b border-[#D9E1E3] bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="terms-brand">
            <Logo size={32} />
            <span className="font-heading font-semibold text-lg">CoParent Console</span>
          </Link>
          <Link to="/" className="text-sm text-[#6F828A] hover:text-[#1F9FA4] inline-flex items-center gap-1" data-testid="terms-back-home"><ArrowLeft className="w-4 h-4" /> Home</Link>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-testid="terms-page">
        <div>
          <div className="text-xs uppercase tracking-[0.12em] text-[#6F828A]">Legal</div>
          <h1 className="font-heading text-4xl font-semibold mt-2">Terms of Use</h1>
          <p className="text-sm text-[#6F828A] mt-2" data-testid="terms-last-updated">Last updated: 29 April 2026</p>
        </div>

        <Section title="1. Agreement" id="agreement">
          <p>
            These Terms of Use (&quot;Terms&quot;) are a binding agreement
            between you and CoParent Console (the &quot;Service&quot;,
            &quot;we&quot;, &quot;us&quot;, &quot;our&quot;). By creating an
            account or using the Service you agree to these Terms and to our{" "}
            <Link to="/privacy" className="text-[#1F9FA4] hover:text-[#178085]">
              Privacy Policy
            </Link>. If you do not agree, do not use the Service.
          </p>
          <p>
            CoParent Console is operated from Ireland and built with a strong
            focus on privacy, data protection, and family wellbeing.
          </p>
        </Section>

        <Section title="2. Who can use the Service" id="who">
          <p>
            You must be at least 18 years old to create an account. You confirm
            that the information you provide is accurate, that you have the
            authority to share household data with your co-parent, and that any
            information you record about children is provided lawfully.
          </p>
          <p>
            The Service is intended for adults. There are no direct accounts
            for children. You are responsible for the information you input
            about children.
          </p>
        </Section>

        <Section title="3. Accounts, security and access" id="accounts">
          <p>
            You are responsible for keeping your credentials confidential and
            for all activity under your account. We strongly recommend
            enabling two-factor authentication. Notify us promptly of any
            suspected unauthorised access by emailing{" "}
            <a href="mailto:support@coparentconsole.com" className="text-[#1F9FA4] hover:text-[#178085]">
              support@coparentconsole.com
            </a>.
          </p>
          <p>
            You may invite one co-parent per child using an invite code. You
            may also generate time-limited, view-only invite codes for
            mediators or solicitors. You are responsible for who you invite
            and for revoking access when it is no longer appropriate.
          </p>
        </Section>

        <Section title="4. Accuracy of data" id="accuracy">
          <p>
            You are responsible for the accuracy of the data you enter into
            the Service. CoParent Console does not verify the correctness of
            any entries, including messages, expense amounts, journal entries,
            calendar events or child profile details.
          </p>
        </Section>

        <Section title="5. Your content" id="content">
          <p>
            You retain all rights to the content you create (messages, journal
            entries, uploaded documents and similar). You grant CoParent
            Console a worldwide, royalty-free, non-exclusive licence to host,
            process, display and transmit your content strictly for the
            purpose of providing the Service to you and your invited users.
            This licence ends when your content is deleted from our systems.
          </p>
        </Section>

        <Section title="6. Immutability of records" id="immutability">
          <p>
            A core purpose of the Service is to create a reliable,
            contemporaneous record. Once created,{" "}
            <strong>messages cannot be edited or deleted</strong>, and key
            actions are written to an immutable audit log. You accept this
            behaviour as a fundamental feature of the Service. You are
            responsible for considering this before sending any message or
            taking any recorded action.
          </p>
        </Section>

        <Section title="7. Acceptable use" id="acceptable-use">
          <p>You must not use the Service to:</p>
          <ul className="list-disc pl-6 space-y-1.5">
            <li>Harass, threaten, stalk, defame or incite violence against any person.</li>
            <li>Upload content that is unlawful, infringes intellectual-property rights, or contains malware.</li>
            <li>Circumvent security, scrape data at scale, or attempt to access accounts or households other than your own.</li>
            <li>Use the Service to violate any court order, protective order or similar legal instrument.</li>
            <li>Resell the Service or use it to provide a competing service.</li>
          </ul>
          <p>We may suspend or terminate accounts that breach these rules.</p>
        </Section>

        <Section title="8. Not legal advice" id="not-legal-advice">
          <p>
            CoParent Console does not provide legal advice. Any information
            generated by the platform, including reports or exports, should
            not be considered legal advice. Whether such records are
            admissible or persuasive in any legal proceeding depends on the
            rules of the relevant jurisdiction.
          </p>
        </Section>

        <Section title="9. Free trial, subscription, billing and cancellation" id="subscription">
          <p data-testid="terms-trial-summary">
            New accounts include a <strong>30-day free trial</strong> with
            full access to every day-to-day feature — messaging, calendar,
            expenses, journal, documents, contact visits and everything
            else you use inside the app — with no storage limits and no
            payment details required to start. From day one,{" "}
            <strong>
              exporting PDFs, downloading reports, generating a Court
              Bundle, exporting data and sharing records externally
            </strong>{" "}
            (for example inviting a mediator or solicitor) are paid-only
            capabilities that unlock when you subscribe. You may cancel
            during the trial at any time, free of charge.
          </p>
          <p data-testid="terms-grace-summary">
            If you do not subscribe by the end of the trial, your account will
            move to a <strong>7-day read-only grace period</strong>. During
            this period you can sign in, view every record, review Insights
            and browse your Court Bundles and Documents, and subscribe to
            reactivate full access at any time. You cannot create new
            messages, calendar events, expenses, journal entries, documents
            or invitations during this period, and the paid-only capabilities
            above remain paid-only.
          </p>
          <p data-testid="terms-lock-summary">
            If no subscription is activated by the end of the grace period,
            your account will be <strong>locked</strong>. Sign-in will
            continue to work; you will retain the same read-only view of your
            records and will see a clear &ldquo;Subscribe to regain
            access&rdquo; prompt and a support contact link. Your data is
            preserved indefinitely — nothing is deleted — so that you can
            resume where you left off whenever you choose to subscribe.
          </p>
          <p data-testid="terms-billing-summary">
            If you choose to subscribe, paid subscriptions are billed at{" "}
            <strong>€10.99 per month</strong> (with a minimum commitment of
            three months) or <strong>€99 per year</strong> billed in advance.
            The annual plan has no monthly minimum because the upfront payment
            itself covers the commitment period. After the initial three-month
            period on the monthly plan, your subscription continues on a
            rolling monthly basis unless cancelled. On the annual plan, your
            subscription renews automatically at the end of each year unless
            you cancel. You may cancel a monthly plan at any time after the
            initial three-month commitment period. You may cancel an annual
            plan at any time. Cancellation takes effect at the end of the
            current billing period (the end of the current month for monthly
            subscribers, or the end of the paid year for annual subscribers).
            After cancellation, your account moves into the same 7-day
            read-only grace period and then locks as described above. No
            partial refunds are provided for unused time on either plan.
          </p>
          <ul className="list-disc pl-6 space-y-1.5">
            <li>Free trial: 30 days, no card required, full access to every day-to-day feature apart from the paid-only capabilities listed above.</li>
            <li>Read-only grace period after trial / cancellation: 7 days.</li>
            <li>Account lock if no subscription is activated by the end of the grace period.</li>
            <li>Data retention after lock: indefinite — your data is preserved and nothing is deleted while your account remains locked.</li>
            <li>Monthly plan: €10.99 per month.</li>
            <li>Annual plan: €99 per year, billed in advance (saves €32.88 vs. paying monthly for the same period).</li>
            <li>Minimum commitment on the monthly plan: 3 months (total €32.97).</li>
            <li>No monthly minimum on the annual plan; the upfront annual payment satisfies the commitment.</li>
            <li>You cannot cancel within the first 3 months of paid use on the monthly plan.</li>
            <li>You can cancel the annual plan at any time, and your access continues until the end of the year you have paid for.</li>
            <li>After the monthly minimum, you can cancel the monthly plan at any time.</li>
            <li>Cancellation takes effect at the end of the current billing period (month or year).</li>
            <li>No partial refunds are provided for unused time on either plan.</li>
          </ul>
          <p>
            Payments are processed by Stripe. Your card details are handled
            directly by Stripe and never stored on our servers. You can manage
            your subscription from the Settings page in-app.
          </p>
          <p data-testid="terms-viewer-summary">
            Viewer access granted to mediators, solicitors or other
            professionals you invite is a separate, granter-led arrangement
            and is not affected by the trial, grace-period or subscription
            cycle described above. Viewer access depends on the inviting
            parent&apos;s account remaining active. If a parent&apos;s account
            is deleted or anonymised, related viewer access ends with it.
          </p>
        </Section>

        <Section title="10. Availability, changes and interruptions" id="availability">
          <p>
            We aim to keep the Service available, but we do not guarantee
            uninterrupted operation. The Service may be updated, changed,
            suspended or interrupted from time to time — for example, for
            maintenance, security updates, or to release new features. Where
            changes are material we will provide reasonable notice.
          </p>
        </Section>

        <Section title="11. Third-party services" id="third-party">
          <p>
            The Service integrates with third parties (for example payment
            processing, email delivery and object storage) whose performance
            is outside our direct control. We are not responsible for
            third-party outages or changes to their services, but we will act
            reasonably to restore functionality.
          </p>
        </Section>

        <Section title="12. Termination" id="termination">
          <p>
            You may close your account at any time by contacting us, subject
            to the subscription terms in section 9. We may close or suspend
            accounts for material breach of these Terms, legal reasons, or
            extended inactivity after reasonable notice. On termination, we
            will retain data only as set out in the Privacy Policy.
          </p>
        </Section>

        <Section title="13. Disclaimers" id="disclaimers">
          <p>
            To the maximum extent permitted by law, the Service is provided
            &quot;as is&quot; and &quot;as available&quot;. We exclude all
            warranties not expressly set out in these Terms, including implied
            warranties of merchantability, fitness for a particular purpose
            and non-infringement. Nothing in these Terms limits liability for
            death or personal injury caused by negligence, for fraud, or for
            any other liability that cannot be excluded under Irish law.
          </p>
        </Section>

        <Section title="14. Limitation of liability" id="liability">
          <p>
            To the maximum extent permitted by law, CoParent Console will not
            be liable for indirect, consequential, special, exemplary or
            punitive damages, nor for loss of profits, business, goodwill or
            anticipated savings.
          </p>
          <p>
            We are not responsible for disputes between users of the Service.
            CoParent Console provides tools for record-keeping and
            communication; how those tools are used between co-parents,
            mediators or solicitors is a matter for those individuals.
          </p>
          <p>
            Our total aggregate liability arising out of or in connection with
            the Service (whether in contract, tort or otherwise) is limited to
            the greater of (a) the fees you paid for the Service in the twelve
            months preceding the event giving rise to the claim, and (b){" "}
            <strong>one hundred euro (€100)</strong>.
          </p>
        </Section>

        <Section title="15. Indemnity" id="indemnity">
          <p>
            You agree to indemnify CoParent Console against any third-party
            claims arising from your content, your use of the Service in
            breach of these Terms, or your breach of Data Protection Law, to
            the extent such claims are caused by you.
          </p>
        </Section>

        <Section title="16. Governing law and jurisdiction" id="governing-law">
          <p>
            This agreement is governed by the laws of Ireland. The courts of
            Ireland have exclusive jurisdiction to settle any dispute or claim
            arising out of or in connection with these Terms. If you are a
            consumer resident in another EU country, you retain the benefit of
            any mandatory consumer-protection rights in your country of
            residence.
          </p>
        </Section>

        <Section title="17. Changes to these Terms" id="changes">
          <p>
            We may update these Terms from time to time. Material changes will
            be notified in-app or by email and take effect after reasonable
            notice. Continued use of the Service after changes take effect
            constitutes acceptance.
          </p>
        </Section>

        <Section title="18. Contact" id="contact">
          <p>
            Questions about these Terms:{" "}
            <a href="mailto:support@coparentconsole.com" className="text-[#1F9FA4] hover:text-[#178085]">
              support@coparentconsole.com
            </a>.
          </p>
        </Section>
      </main>
    </div>
  );
}
